import { useState, useEffect, useRef } from "react";

export const useStageManagement = (courses: any[]) => {
  // State for available stages based on selected course
  const [availableStages, setAvailableStages] = useState<
    { id: number; name: string }[]
  >([]);

  // Refs to track previous values and prevent infinite loops
  const prevCourseIdRef = useRef<number | null>(null);
  const prevCoursesLengthRef = useRef<number>(0);
  const isInitialRender = useRef<boolean>(true);
  const isUpdatingStage = useRef<boolean>(false);

  // Update available stages when course changes
  const updateAvailableStages = (
    selectedCourseId: number | null,
    selectedStage: string,
    setSelectedStage: (stage: string) => void
  ) => {
    // Skip if we're already in the middle of an update
    if (isUpdatingStage.current) return;

    // Skip if nothing has changed
    if (
      !isInitialRender.current &&
      selectedCourseId === prevCourseIdRef.current &&
      courses.length === prevCoursesLengthRef.current
    ) {
      return;
    }

    isUpdatingStage.current = true;

    try {
      // Update refs
      prevCourseIdRef.current = selectedCourseId;
      prevCoursesLengthRef.current = courses.length;
      isInitialRender.current = false;

      if (selectedCourseId) {
        const selectedCourse = courses.find(
          (course) => course.id === selectedCourseId
        );

        if (selectedCourse && selectedCourse.courseStages) {
          setAvailableStages(selectedCourse.courseStages);

          // If stage is not selected but we have one in session storage, restore it
          if (!selectedStage && courses.length > 0) {
            // Get stored state to check for stage
            const storedState = sessionStorage.getItem(
              "pay_share_filter_state"
            );
            if (storedState) {
              try {
                const parsedState = JSON.parse(storedState);
                if (parsedState?.selectedStage) {
                  // Only set the stage if it exists in the available stages and is different
                  const stageExists = selectedCourse.courseStages.some(
                    (stage: any) =>
                      stage.id.toString() === parsedState.selectedStage
                  );
                  if (
                    stageExists &&
                    parsedState.selectedStage !== selectedStage
                  ) {
                    setSelectedStage(parsedState.selectedStage);
                  }
                }
              } catch (e) {
                console.error("Error parsing stored filter state:", e);
              }
            }
          }
        } else {
          setAvailableStages([]);
        }
      } else {
        setAvailableStages([]);
        if (selectedStage !== "") {
          setSelectedStage("");
        }
      }
    } finally {
      // Clear the update flag after a short delay to prevent race conditions
      setTimeout(() => {
        isUpdatingStage.current = false;
      }, 0);
    }
  };

  return {
    availableStages,
    setAvailableStages,
    updateAvailableStages,
  };
};
